import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  BarChart3,
  ChevronRight,
  ChevronLeft,
  X,
  Rocket,
  Shield,
  Trophy,
  Lightbulb,
  Bot,
  Settings,
} from "lucide-react";
import { cn } from "@/lib/utils";

const ONBOARDING_KEY = "mm-onboarding-seen";

const steps = [
  {
    icon: Rocket,
    title: "Welcome to Margin Metrics",
    description:
      "Your all-in-one trading analytics platform. Let's take a quick tour to help you get the most out of it.",
    tip: null,
    color: "text-primary",
    bg: "",
    nav: null,
  },
  {
    icon: BookOpen,
    title: "Log Your Trades",
    description:
      "Use the Trade Journal to record entries, exits, emotions, and strategies. This is the foundation -- all your analytics and AI insights are powered by your journal data.",
    tip: "Pro tip: Use the CSV import to bulk-upload existing trades from MT4, MT5, or any broker export.",
    color: "text-primary",
    bg: "",
    nav: "/journal",
  },
  {
    icon: BarChart3,
    title: "Analyse Your Performance",
    description:
      "Your equity curve, win rate breakdowns, session heatmaps, and strategy analysis update automatically as you log trades. Use the date range filter to compare different periods.",
    tip: "Pro tip: Filter by instrument or session to find exactly where your edge is strongest.",
    color: "text-white/70",
    bg: "bg-white/5",
    nav: "/analytics",
  },
  {
    icon: Shield,
    title: "Prop Firm Guard",
    description:
      "Add your prop firm account rules and Margin Metrics will track your daily loss limits, max drawdown, and consistency -- alerting you before you breach.",
    tip: "Pro tip: Assign trades to prop accounts in the journal to track per-account performance separately.",
    color: "text-amber-400",
    bg: "bg-amber-400/10",
    nav: "/prop-guard",
  },
  {
    icon: Bot,
    title: "Meet Your AI Agents",
    description:
      "Navigate to the Agents page in the sidebar to access your AI team. Seven specialised agents -- Risk, Coach, Pattern, Journal, News, Calendar, and Trainer -- each with a distinct role. They analyse your trade data and post personalised insights.",
    tip: "Pro tip: Ask any agent a direct question in the chat panel to get analysis specific to your situation.",
    color: "text-[#1d9e75]",
    bg: "bg-[#1d9e75]/10",
    nav: "/agents",
  },
  {
    icon: Trophy,
    title: "Edge Score",
    description:
      "Track your trading edge over time with a composite score based on risk management, consistency, win rate, and discipline. It rates your performance 300-850 -- like a credit score for your trading.",
    tip: "Pro tip: Unlock achievements as your Edge Score improves to gamify your development.",
    color: "text-purple-400",
    bg: "bg-purple-400/10",
    nav: "/edge-score",
  },
  {
    icon: Settings,
    title: "Broker & Account Settings",
    description:
      "Head to Settings to connect your MT4/5, OANDA, or Tradovate account for auto-synced trades. You can also manage your risk parameters, notifications, and appearance preferences here.",
    tip: "Pro tip: Enable CSV auto-import to keep your journal up to date with minimal manual entry.",
    color: "text-amber-400",
    bg: "bg-amber-400/10",
    nav: "/settings",
  },
];

export function WelcomeModal() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem(ONBOARDING_KEY)) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(ONBOARDING_KEY, "true");
  };

  const handleNext = () => {
    if (step === steps.length - 1) {
      dismiss();
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleGoToPage = () => {
    const nav = steps[step].nav;
    if (nav) {
      dismiss();
      navigate(nav);
    }
  };

  if (!visible) return null;

  const current = steps[step];
  const Icon = current.icon;
  const isLast = step === steps.length - 1;
  const isFirst = step === 0;
  const progress = ((step + 1) / steps.length) * 100;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 animate-fade-in">
      <div className="glass-card w-full max-w-md relative" style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        {/* Progress bar */}
        <div className="h-1 bg-muted w-full shrink-0">
          <div
            className="h-full bg-primary transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Scrollable content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Top glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

          <button
            onClick={dismiss}
            className="absolute top-5 right-5 p-1.5 hover:bg-secondary/50 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Step counter */}
          <p className="text-[10px] text-muted-foreground font-medium tracking-wider uppercase mb-4">
            Step {step + 1} of {steps.length}
          </p>

          <div className="space-y-4">
            {/* Step indicator dots */}
            <div className="flex items-center gap-1.5">
              {steps.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setStep(i)}
                  className={cn(
                    "h-1.5 transition-all duration-300 cursor-pointer hover:opacity-80",
                    i === step
                      ? "w-8 bg-primary"
                      : i < step
                      ? "w-3 bg-primary/40"
                      : "w-2 bg-muted-foreground/20"
                  )}
                />
              ))}
            </div>

            <div className={cn("p-4 w-fit", current.bg)}>
              <Icon className={cn("w-10 h-10", current.color)} />
            </div>

            <h2 className="text-xl font-bold">{current.title}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {current.description}
            </p>

            {/* Pro tip */}
            {current.tip && (
              <div className="flex items-start gap-2.5 p-3 bg-primary/5 border border-primary/10">
                <Lightbulb className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {current.tip}
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-6 gap-3">
            <div className="flex items-center gap-2">
              {!isFirst && (
                <button
                  onClick={handleBack}
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  Back
                </button>
              )}
              <button
                onClick={dismiss}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-2"
              >
                Skip tour
              </button>
            </div>

            <div className="flex items-center gap-2">
              {current.nav && (
                <button
                  onClick={handleGoToPage}
                  className="text-xs text-primary hover:text-primary/80 transition-colors px-3 py-2 hover:bg-primary/5"
                >
                  Go to page &rarr;
                </button>
              )}
              <button
                onClick={handleNext}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors text-sm"
              >
                {isLast ? (
                  <>
                    <Rocket className="w-4 h-4" />
                    Start Trading
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
